import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, HelpCircle, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { DOBDatePicker } from '../ui/date-picker';
import { useRecaptcha } from '../../hooks/useRecaptcha';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SimpleForgotPasswordModal = ({ open, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // reCAPTCHA hook
  const { executeRecaptcha, isEnabled: recaptchaEnabled, resetRecaptcha } = useRecaptcha('forgot_password');
  
  // Date state
  const [dateOfBirth, setDateOfBirth] = useState(null);
  
  // Anti-spam: Track form start time
  const [formStartTime, setFormStartTime] = useState(null);
  
  // All form data in one state
  const [formData, setFormData] = useState({
    indos_number: '',
    name: '',
    email: '',
    mobile_digits: '',
    new_password: '',
    confirm_password: '',
    website: '' // Honeypot field - should remain empty
  });

  // Step 4: Security questions data
  const [securityQuestions, setSecurityQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [correctAnswers, setCorrectAnswers] = useState(0);
  // Server-issued tokens that gate the final reset. `sessionToken` is minted
  // at step-4-get-question and threaded through every step-4-verify call.
  // `resetToken` is returned only on the 3rd correct answer and is what the
  // /reset-password endpoint requires.
  const [sessionToken, setSessionToken] = useState('');
  const [resetToken, setResetToken] = useState('');

  // Set form start time when modal opens
  useEffect(() => {
    if (open && !formStartTime) {
      setFormStartTime(Date.now() / 1000);
    }
  }, [open, formStartTime]);

  if (!open) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleStep1 = async () => {
    if (!formData.indos_number) {
      toast.error('Please enter your INDoS number');
      return;
    }

    const indosPattern = /^[0-9]{2}[A-Za-z]{2}[0-9]{4}$/;
    if (!indosPattern.test(formData.indos_number)) {
      toast.error('Invalid INDoS number format');
      return;
    }

    setLoading(true);
    try {
      // Execute reCAPTCHA if enabled
      let recaptchaToken = null;
      if (recaptchaEnabled) {
        recaptchaToken = await executeRecaptcha();
        if (!recaptchaToken) {
          toast.error('Please complete the security verification');
          setLoading(false);
          return;
        }
      }
      
      await axios.post(`${API}/auth/forgot-password-step1`, {
        indos_number: formData.indos_number,
        website: formData.website, // Include honeypot
        form_timestamp: formStartTime, // Include start time for total duration tracking
        recaptcha_token: recaptchaToken
      });
      toast.success('INDoS verified! Please provide your details.');
      setStep(2);
    } catch (error) {
      resetRecaptcha();
      toast.error(error.response?.data?.detail || 'INDoS number not found');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async () => {
    // Validate all fields
    if (!formData.name || !formData.email || !dateOfBirth) {
      toast.error('Please fill all fields');
      return;
    }

    // Validate email
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      // Convert date to YYYY-MM-DD for backend
      const formattedDate = format(dateOfBirth, 'yyyy-MM-dd');
      
      await axios.post(`${API}/auth/forgot-password-step2`, {
        indos_number: formData.indos_number,
        name: formData.name,
        email: formData.email,
        date_of_birth: formattedDate
      });
      
      toast.success('Details verified! Proceed to mobile verification.');
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleStep3 = async () => {
    if (!formData.mobile_digits || formData.mobile_digits.length !== 4) {
      toast.error('Please enter last 4 digits');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password-step3`, {
        indos_number: formData.indos_number,
        mobile_digits: formData.mobile_digits
      });
      
      toast.success('Mobile verified! Answer security questions.');
      
      // Get security questions
      const questionsResponse = await axios.post(`${API}/auth/forgot-password-step4-get-question`, {
        indos_number: formData.indos_number
      });
      
      setSecurityQuestions(questionsResponse.data.questions);
      setSessionToken(questionsResponse.data.session_token || '');
      setResetToken('');
      setCurrentQuestionIndex(0);
      setCorrectAnswers(0);
      setStep(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Mobile verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleStep4 = async () => {
    if (!currentAnswer.trim()) {
      toast.error('Please provide an answer');
      return;
    }

    setLoading(true);
    try {
      const verifyRes = await axios.post(`${API}/auth/forgot-password-step4-verify`, {
        indos_number: formData.indos_number,
        question: securityQuestions[currentQuestionIndex].question,
        answer: currentAnswer,
        session_token: sessionToken,
      });

      const newCorrectCount = correctAnswers + 1;
      setCorrectAnswers(newCorrectCount);

      // Backend returns `reset_token` only on the 3rd correct answer.
      if (verifyRes.data?.reset_token) {
        setResetToken(verifyRes.data.reset_token);
      }

      // Check if we've answered all 3 questions
      if (currentQuestionIndex >= 2) {
        toast.success('All security questions verified!');
        setStep(5);
      } else {
        toast.success(`Correct! ${newCorrectCount} of 3 questions answered.`);
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setCurrentAnswer('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Incorrect answer');
    } finally {
      setLoading(false);
    }
  };

  const handleStep5 = async () => {
    if (!formData.new_password || !formData.confirm_password) {
      toast.error('Please fill all fields');
      return;
    }

    if (formData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      if (!resetToken) {
        toast.error('Session expired. Please restart the reset flow.');
        setLoading(false);
        return;
      }
      await axios.post(`${API}/auth/reset-password`, {
        reset_token: resetToken,
        indos_number: formData.indos_number,
        new_password: formData.new_password,
        form_timestamp: formStartTime // Include start time for total duration check (> 10 seconds)
      });
      
      toast.success('Password reset successful! You can now log in.');
      resetAndClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setStep(1);
    setFormData({
      indos_number: '',
      name: '',
      email: '',
      mobile_digits: '',
      new_password: '',
      confirm_password: '',
      website: '' // Reset honeypot
    });
    setDateOfBirth(null);
    setFormStartTime(null); // Reset form start time
    setSecurityQuestions([]);
    setCurrentQuestionIndex(0);
    setCurrentAnswer('');
    setCorrectAnswers(0);
    setShowPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900">
            Reset Password - Step {step} of 5
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">

        {/* Step 1 */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                Enter your INDoS number to begin password recovery.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                INDoS Number
              </label>
              <input
                type="text"
                value={formData.indos_number}
                onChange={(e) => handleInputChange('indos_number', e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && !loading && handleStep1()}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your INDoS"
                maxLength={8}
                autoComplete="off"
              />
            </div>

            {/* Honeypot field - invisible to users, bots will fill it */}
            <input
              type="text"
              name="website"
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              autoComplete="off"
              tabIndex={-1}
              style={{ 
                position: 'absolute',
                left: '-9999px',
                opacity: 0,
                height: 0,
                width: 0
              }}
              aria-hidden="true"
            />

            <button
              onClick={handleStep1}
              disabled={loading}
              className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Next'}
            </button>

            {/* reCAPTCHA indicator */}
            {recaptchaEnabled && (
              <div className="flex items-center justify-center gap-1.5 text-xs text-gray-500">
                <Shield size={12} className="text-green-600" />
                <span>Protected by reCAPTCHA</span>
              </div>
            )}
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                Verify your identity with registered details.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="As registered"
                autoComplete="off"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="text"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="your@email.com"
                autoComplete="off"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date of Birth
              </label>
              <DOBDatePicker 
                date={dateOfBirth} 
                onDateChange={setDateOfBirth}
                placeholder="Select your date of birth"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleStep2}
                disabled={loading}
                className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Mobile Number Verification */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                Enter the last 4 digits of your registered mobile number.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last 4 Digits of Mobile Number
              </label>
              <input
                type="text"
                value={formData.mobile_digits}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  if (value.length <= 4) {
                    handleInputChange('mobile_digits', value);
                  }
                }}
                onKeyDown={(e) => e.key === 'Enter' && !loading && handleStep3()}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-lg font-mono"
                placeholder="****"
                maxLength={4}
                autoComplete="off"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(2)}
                className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleStep3}
                disabled={loading}
                className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Security Questions */}
        {step === 4 && (
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                Answer your security questions ({correctAnswers}/3 correct)
              </p>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">Question {currentQuestionIndex + 1} of 3:</p>
              <p className="text-base font-medium text-gray-900">
                {securityQuestions[currentQuestionIndex]?.question}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Answer
              </label>
              <input
                type="text"
                value={currentAnswer}
                onChange={(e) => setCurrentAnswer(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !loading && handleStep4()}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your answer"
                autoComplete="off"
              />
            </div>

            <button
              onClick={handleStep4}
              disabled={loading}
              className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Submit Answer'}
            </button>
          </div>
        )}

        {/* Step 5: Reset Password */}
        {step === 5 && (
          <div className="space-y-4">
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-sm text-green-800">
                All verifications complete! Set your new password.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.new_password}
                  onChange={(e) => handleInputChange('new_password', e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !loading && handleStep5()}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="At least 8 characters"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirm_password}
                  onChange={(e) => handleInputChange('confirm_password', e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !loading && handleStep5()}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Re-enter password"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              onClick={handleStep5}
              disabled={loading}
              className="w-full py-2 px-4 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        )}

        {/* Contact Us Link - shown on all steps */}
        <div className="text-center pt-2 border-t">
          <Link 
            to="/contact-us" 
            onClick={resetAndClose}
            className="text-sm text-gray-500 hover:text-blue-600 inline-flex items-center gap-1"
          >
            <HelpCircle size={14} />
            Need help? Contact us
          </Link>
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SimpleForgotPasswordModal;